// Intent: history must be TRUE, so we record what the app actually predicted
// on each day instead of reconstructing it later from today's amounts. One KV
// read per app start, one write per calendar day — no timers, no per-render
// work. Hooked after store hydration and on AppState 'active' (an app left
// open across midnight would otherwise skip a day).

import { getDb } from '@/db';
import { useBudgetStore } from '@/store/budget-store';
import { useFixedCostsStore } from '@/store/fixed-costs-store';
import { useForecastHistoryStore } from '@/store/forecast-history-store';
import { useIncomeStore } from '@/store/income-store';
import type { ForecastSnapshot } from '@/types/domain';
import { monthForecastOf } from '@/utils/forecast';
import { todayIso } from '@/utils/format';

export const KV_LAST_SNAPSHOT_DAY = 'forecast_snapshot_last_day';

export async function recordForecastSnapshotIfNeeded(now = new Date()): Promise<void> {
  const day = todayIso(now);
  const db = await getDb();
  const last = await db.getKv(KV_LAST_SNAPSHOT_DAY);
  if (last === day) return;

  // Stores are hydrated before this runs (use-app-hydration awaits them all).
  const income = useIncomeStore.getState();
  const budget = useBudgetStore.getState();
  const costs = useFixedCostsStore.getState().costs;
  const f = monthForecastOf(income.sources, income.entries, costs, budget.entries, budget.monthlyBudgetGr, now);

  const snap: ForecastSnapshot = {
    id: day,
    day,
    incomeGr: f.incomeGr,
    outflowGr: f.outflowGr,
    leftGr: f.leftGr,
    createdAt: new Date().toISOString(),
  };
  await useForecastHistoryStore.getState().record(snap);
  await db.setKv(KV_LAST_SNAPSHOT_DAY, day);
}
